import { API_BASE_URL, CUSTOMER_BASE_URL, LOGOUT_URL, SUPPLIER_BASE_URL, defaultLocale } from '@synergy/config';
import {
    AddUserIcon,
    ChangeIcon,
    FlagIcon,
    HeartIcon,
    KeyIcon,
    LayoutIcon,
    LockIcon,
    LogoutIcon,
    NoticeIcon,
    ProfileIcon,
    SendIcon,
    ShoppingCartIcon,
    theme,
} from '@synergy/theme-next';
import {
    Collapse,
    Divider,
    IconButton,
    Link,
    List,
    ListItem,
    ListItemIcon,
    ListItemText,
    MenuItem,
    PopoverProps,
    Stack,
    styled,
    useTheme,
} from '@mui/material';
import { FormattedMessage } from 'react-intl';
import { Locale, UserRole } from '@synergy/models';
import { MouseEvent, ReactElement, ReactNode, useState } from 'react';
import {
    PoolButton,
    PoolModal,
    PoolModalActions,
    PoolModalContent,
    PoolModalTitle,
    PoolSelect,
    PoolTypography,
} from '@synergy/components-next';
import { useNavigate } from 'react-router';
import urlJoin from 'url-join';

import { AccessControl } from '../../../access-control/access-control';
import { AccountMenuItem } from './account-menu-item';
import { ROUTES } from '../../../../utils/routes';
import { UserItem } from './user-item';
import {
    accountSelector,
    accountsSelector,
    headerSelector,
    userSelector,
} from '../../../../store/configuration-selectors';
import { changeAccount, changeLanguage } from '../../../../store/configuration-actions';
import { openChangePasswordModal } from '../../../../store/change-password-actions';
import { useAppDispatch, useAppSelector } from '../../../../../store/store';

const Headline = ({ title }: { title: ReactNode }): ReactElement => {
    return (
        <ListItem
            sx={{
                color: theme.palette.grey[500],
                padding: '24px 16px 6px',
            }}
        >
            <PoolTypography variant="statusLabel">{title}</PoolTypography>
        </ListItem>
    );
};

const MenuDivider = styled(Divider)(({ theme }) => ({
    '&&.MuiDivider-root': {
        border: 0,
        margin: 0,
        borderTop: `1px solid ${theme.palette.grey[700]}`,
    },
}));

interface AccountMenuContentProps {
    onClose?: PopoverProps['onClose'];
}

export const AccountMenuContent = (props: AccountMenuContentProps): ReactElement | null => {
    const theme = useTheme();
    const account = useAppSelector(accountSelector);
    const accounts = useAppSelector(accountsSelector);
    const user = useAppSelector(userSelector);
    const header = useAppSelector(headerSelector);
    const locales = useAppSelector(state => state.configuration.supportedLocales);
    const isImpersonator = useAppSelector(state => state.configuration.isImpersonator);
    const [isOpen, setIsOpen] = useState(false);
    const [isLanguageModalOpen, setIsLanguageModalOpen] = useState(false);
    const [selectedLanguage, setSelectedLanguage] = useState(user?.locale ?? defaultLocale);
    const dispatch = useAppDispatch();
    const navigate = useNavigate();

    if (!user || !account) {
        return null;
    }
    let logoutUrl = LOGOUT_URL;

    if (isImpersonator) {
        logoutUrl = `${API_BASE_URL}/switch-user?redirect=${encodeURI(window.location.href)}&_switch_user=_exit`;
    }

    const languageOptions = locales.map(locale => {
        return {
            value: locale,
            displayValue: <FormattedMessage id={`app.common.${locale}`} />,
        };
    });

    const handleNavigationCustomerClick = (path: string): void => {
        window.open(urlJoin(CUSTOMER_BASE_URL, path), '_self');
    };

    const handleNavigationSupplierClick = (path: string): void => {
        window.open(urlJoin(SUPPLIER_BASE_URL, path), '_self');
    };

    const handleAccountChange = async (accountId: number): Promise<void> => {
        await dispatch(changeAccount(accountId));

        window.location.reload();
    };

    const handleWishListClick = (e: MouseEvent<HTMLLIElement>): void => {
        props.onClose?.(e, 'backdropClick');
        navigate(ROUTES.wishList.base);
    };

    const handlePasswordChange = (e: MouseEvent<HTMLLIElement>): void => {
        props.onClose?.(e, 'backdropClick');
        dispatch(openChangePasswordModal());
    };

    const handleLanguageChange = async (): Promise<void> => {
        await dispatch(changeLanguage(selectedLanguage));
        setIsLanguageModalOpen(false);

        window.location.reload();
    };

    const handleSupplierProfileClick = (e: MouseEvent): void => {
        navigate(`supplier-directory/${account.id}`);
        props.onClose?.(e, 'backdropClick');
    };

    return (
        <>
            <UserItem firstName={user.firstName} lastName={user.lastName} accountName={account.name}>
                {accounts.length > 1 && (
                    <IconButton onClick={() => setIsOpen(prev => !prev)} color="light">
                        <ChangeIcon />
                    </IconButton>
                )}
            </UserItem>
            {accounts.length > 1 && (
                <Collapse in={isOpen} timeout="auto" unmountOnExit={true}>
                    <MenuDivider />
                    <List component="div" disablePadding={true}>
                        {accounts
                            .filter(a => a.id !== account?.id)
                            .map(a => (
                                <UserItem key={a.id} onClick={() => handleAccountChange(a.id)} accountName={a.name} />
                            ))}
                    </List>
                </Collapse>
            )}

            <Headline title={<FormattedMessage id="app.accountMenu.myAccount" />} />
            <AccessControl user={user} allowedRoles={[UserRole.ShopAdmin, UserRole.ShopUser]}>
                <AccountMenuItem
                    icon={<HeartIcon />}
                    text={<FormattedMessage id="app.accountMenu.wishList" />}
                    onClick={handleWishListClick}
                    data-testid="wishList"
                />
            </AccessControl>
            <AccountMenuItem
                icon={<KeyIcon />}
                text={<FormattedMessage id="app.accountMenu.changePassword" />}
                onClick={handlePasswordChange}
            />
            <AccountMenuItem
                icon={<FlagIcon />}
                text={<FormattedMessage id="app.accountMenu.changeLanguage" />}
                onClick={() => setIsLanguageModalOpen(true)}
            />

            <Divider />

            {/* Tracking and managing live in the member portal, buying here
                (GF 15.10.2026, #2282): every way there says that it leaves. */}
            <AccessControl user={user} allowedRoles={[UserRole.ShopAdmin, UserRole.ShopUser]}>
                <Headline title={<FormattedMessage id="app.accountMenu.memberPortal" />} />
                <AccountMenuItem
                    icon={<ShoppingCartIcon />}
                    text={<FormattedMessage id="app.accountMenu.orders" />}
                    onClick={() => handleNavigationCustomerClick('orders')}
                    external={true}
                />
                <AccountMenuItem
                    icon={<ShoppingCartIcon />}
                    text={<FormattedMessage id="app.accountMenu.buyAgain" />}
                    onClick={() => handleNavigationCustomerClick('buy-again')}
                    external={true}
                />
                <AccountMenuItem
                    icon={<LockIcon />}
                    text={<FormattedMessage id="app.accountMenu.orderApproval" />}
                    badgeCount={header.pendingSalesOrderCount}
                    onClick={() => handleNavigationCustomerClick('order-approvals')}
                    external={true}
                />
                <AccountMenuItem
                    icon={<SendIcon />}
                    text={<FormattedMessage id="app.accountMenu.inquiries" />}
                    onClick={() => handleNavigationCustomerClick('inquiries')}
                    external={true}
                />
                <AccountMenuItem
                    icon={<NoticeIcon />}
                    text={<FormattedMessage id="app.accountMenu.demands" />}
                    badgeCount={header.ongoingDemandCount}
                    onClick={() => handleNavigationCustomerClick('demands')}
                    external={true}
                />
            </AccessControl>
            <AccessControl user={user} allowedRoles={[UserRole.ShopAdmin]}>
                <AccountMenuItem
                    icon={<AddUserIcon />}
                    text={<FormattedMessage id="app.accountMenu.employees" />}
                    onClick={() => handleNavigationCustomerClick('user-contacts')}
                    external={true}
                />
            </AccessControl>
            <AccessControl user={user} allowedRoles={[UserRole.SupplierAdmin, UserRole.Extranet]}>
                <Headline title={<FormattedMessage id="app.accountMenu.myCompany" />} />
                <AccountMenuItem
                    icon={<ProfileIcon />}
                    text={<FormattedMessage id="app.accountMenu.mySupplierProfile" />}
                    onClick={handleSupplierProfileClick}
                />
                <AccountMenuItem
                    icon={<LayoutIcon />}
                    text={<FormattedMessage id="app.accountMenu.supplierPortal" />}
                    onClick={() => handleNavigationSupplierClick('/')}
                    external={true}
                />
            </AccessControl>

            <Divider />

            <Link href={logoutUrl} sx={{ textDecoration: 'none' }}>
                <MenuItem sx={{ justifyContent: 'flex-end', color: theme.palette.common.black }}>
                    <ListItemIcon>
                        <LogoutIcon />
                    </ListItemIcon>
                    <ListItemText sx={{ flexGrow: 0 }}>
                        <FormattedMessage
                            id={isImpersonator ? 'app.navigation.exitImpersonator' : 'app.navigation.logout'}
                        />
                    </ListItemText>
                </MenuItem>
            </Link>

            <PoolModal open={isLanguageModalOpen} onClose={() => setIsLanguageModalOpen(false)}>
                <PoolModalTitle>
                    <FormattedMessage id="app.modal.title.changeLanguage" />
                </PoolModalTitle>
                <PoolModalContent>
                    <Stack gap={5}>
                        <FormattedMessage id="app.modal.content.changeLanguage" />
                        <PoolSelect
                            value={selectedLanguage}
                            fullWidth={true}
                            required={true}
                            options={languageOptions}
                            label={<FormattedMessage id="app.select.label.changeLanguage" />}
                            onChange={e => setSelectedLanguage(e.target.value as Locale)}
                        />
                    </Stack>
                </PoolModalContent>
                <PoolModalActions>
                    <PoolButton variant="outlined" onClick={() => setIsLanguageModalOpen(false)}>
                        <FormattedMessage id="app.common.cancel" />
                    </PoolButton>
                    <PoolButton onClick={handleLanguageChange}>
                        <FormattedMessage id="app.common.save" />
                    </PoolButton>
                </PoolModalActions>
            </PoolModal>
        </>
    );
};
